import { useTranslation } from 'react-i18next';
import { Trash2, TriangleAlert } from 'lucide-react';
import { ConfirmByTypingDialog } from '@/components/ConfirmByTypingDialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { ApiError, useDeleteAccount, useMe } from '@/features/auth/api';
import { SettingsCard } from '@/features/settings/SettingsCard';
import { cn } from '@/lib/utils';

export function DeleteAccountSection({ className }: { className?: string }) {
  const { t } = useTranslation();
  const me = useMe();
  const deleteAccount = useDeleteAccount();
  const email = me.data?.email;

  if (!email) return null;

  const onConfirm = () => {
    deleteAccount.mutate(
      { confirmEmail: email },
      {
        onError: (error) => {
          const message =
            error instanceof ApiError && error.code === 'LAST_ADMIN'
              ? t('settings.danger.last_admin')
              : t('settings.danger.failed');
          toast.error(message);
        },
      },
    );
  };

  return (
    <SettingsCard
      icon={TriangleAlert}
      title={t('settings.danger.title')}
      description={t('settings.danger.description')}
      className={cn('border-destructive/30', className)}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">{t('settings.danger.delete_account_hint')}</p>
        <ConfirmByTypingDialog
          title={t('settings.danger.confirm_title')}
          description={t('settings.danger.confirm_description', { email })}
          expectedValue={email}
          inputLabel={t('settings.danger.input_label')}
          placeholder={email}
          confirmLabel={t('settings.danger.confirm_button')}
          destructive
          pending={deleteAccount.isPending}
          onConfirm={onConfirm}
        >
          <Button type="button" variant="destructive" className="shrink-0">
            <Trash2 data-icon="inline-start" />
            {t('settings.danger.delete_account')}
          </Button>
        </ConfirmByTypingDialog>
      </div>
    </SettingsCard>
  );
}
